// The display language of the Units Sold surface, in one place.
//
// Ported from Meteoric's `src/lib/units-sold-view.ts` with the numbers
// unchanged - the same figure must read identically in both places while the
// two surfaces overlap, or a reader comparing them concludes one is wrong.

import type { Tone } from "@/lib/tone";
import type { Grain } from "@/lib/meteoric-regions";
import type { GrainBundle, RangeValue } from "@/lib/time-range";

import type {
  AttentionSeverity,
  Product,
  RollingWindow,
  SeriesPoint,
  Tile,
  WindowTotal,
} from "./api";

import { freshnessState } from "@/lib/meteoric-regions";

export {
  bandTone,
  DATA_FRESH_MAX_DAYS,
  DATA_WARN_MAX_DAYS,
  daysSince,
  freshnessState,
  GRAINS,
  isGrain,
  regionFromDeepLink,
  regionLabel,
  regionsFrom,
} from "@/lib/meteoric-regions";
export type { FreshnessState, Grain } from "@/lib/meteoric-regions";

/** Matches read_api.py EXPECTED_MAX_AGE_HOURS (120) and the shell's UTC-midnight rule. */
export const TTU_EXPECTED_MAX_AGE_HOURS = 120;

export function stepFreshness(dataThrough: string | null, now: number = Date.now()) {
  const state = freshnessState(dataThrough);
  if (!dataThrough || state.word === "Stale") return state;
  const asOf = Date.parse(dataThrough);
  if (Number.isNaN(asOf)) return state;
  if ((now - asOf) / 3_600_000 <= TTU_EXPECTED_MAX_AGE_HOURS) {
    return { ...state, tone: "ok" as const, word: "Live" };
  }
  return { ...state, tone: "watch" as const, word: "Lagging" };
}

/** A unit COUNT - always whole, always grouped, never a bare 1234. */
export function formatUnits(value: number | null): string {
  if (value === null) return "n/a";
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value);
}

/** The headline YoY growth figure, in its own unit (`%` from the producer). */
export function formatTileValue(value: number | null, unit: string): string {
  if (value === null) return "n/a";
  return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(value)}${unit}`;
}

/** A signed percentage for a comparison column. */
export function formatSignedPct(value: number | null): string {
  if (value === null) return "n/a";
  const sign = value > 0 ? "+" : "";
  return `${sign}${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(value)}%`;
}

export function formatTtuPct(value: number | null): string {
  if (value === null) return "–";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(Math.abs(value))}%`;
}

export function formatTtuValue(value: number | null, unit: string): string {
  if (value === null) return "–";
  const formatted = new Intl.NumberFormat("en-GB", { maximumFractionDigits: unit === "%" ? 1 : 2 }).format(value);
  return unit === "%" ? `${formatted}%` : formatted;
}

export function channelLabel(channel: string): string {
  return channel.replace(":", " ");
}

export function emptyCopy(noun: string, product: string | null, channels: string[]): string {
  const productLabel = product ?? "any product";
  const channel = channels.length ? channels.map(channelLabel).join(", ") : "any channel";
  return `No ${noun} for ${productLabel} on ${channel} in this range.`;
}

export function formatShortDate(value: string | null): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "unknown";
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return "unknown";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(date);
}

export type AttentionTarget = {
  tab: string;
  search: Record<string, string | string[]>;
};

/** Translate the producer's deep link into the portal tab and URL-search contract. */
export function parseAttentionDeepLink(
  deepLink: string,
  currentSearch: Record<string, unknown> = {},
): AttentionTarget {
  const url = new URL(deepLink, "https://portal.invalid");
  const tab = url.pathname.split("/").filter(Boolean).at(-1) || "traffic";
  const search: Record<string, string | string[]> = {};
  for (const key of ["f_region", "f_range", "f_period"] as const) {
    const value = currentSearch[key];
    if ((typeof value === "string" || typeof value === "number") && String(value)) search[key] = String(value);
  }
  const marketplaces = url.searchParams.getAll("marketplace").filter(Boolean);
  if (marketplaces.length) search.f_channel = marketplaces;
  for (const key of ["product", "source"] as const) {
    const value = url.searchParams.get(key);
    if (value) search[`f_${key}`] = value;
  }
  return { tab, search };
}

/**
 * The change against the prior period, in percentage POINTS - the headline is
 * itself a percentage, and "+12% on a growth rate" is the kind of figure nobody
 * can say the meaning of out loud.
 *
 * An absent delta returns the empty string, and the caller renders nothing. A
 * "0.0" would claim the value held steady, which is a different fact from
 * having no prior period to compare against.
 */
export function formatDeltaPoints(value: number | null): string {
  if (value === null) return "";
  const sign = value > 0 ? "+" : "";
  const arrow = value >= 0 ? "↑" : "↓";
  const formatted = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(value);
  return `${arrow} ${sign}${formatted} pts`;
}

/** Note the vocabulary: this producer emits `warn`, not `warning`. */
export function severityTone(severity: AttentionSeverity): Tone {
  if (severity === "critical") return "critical";
  if (severity === "warn") return "watch";
  return "neutral";
}

/**
 * The rolling window control offers six ranges (`TIME_RANGE_OPTIONS`, shared
 * with Profit Yield's chart range), but the producer only COMPUTES some of
 * them: each window costs 4 BigQuery queries per tile per region, so
 * `ROLLING_WINDOWS` in marketing-analytics grows deliberately.
 *
 * 180 was added 2026-09-17, so 6mo now reads its own window. 365 is still not
 * computed, so 12mo and All time read the widest window below them. That is a
 * stated rule rather than the old hardcoded collapse, and the producer's own
 * PERIOD label ("Last N days · start-end") renders beside the value, so a
 * range backed by a narrower window always says which window backs it.
 */
export type Range = RangeValue;

/** Keep monthly charts useful when the selected rolling window is shorter than a month. */
export function chartRangeFor(grain: Grain, range: Range): Range {
  return grain === "monthly" && (range === "7" || range === "30") ? "365" : range;
}

/** The rolling windows the producer computes, narrowest first. Mirrors
 *  `ROLLING_WINDOWS` in marketing-analytics `units_sold/aggregate.py`. */
export const ROLLING_WINDOW_KEYS = ["7", "30", "90", "180"] as const;

export type RollingWindowKey = (typeof ROLLING_WINDOW_KEYS)[number];

/**
 * The widest computed window that does not exceed the selected range.
 *
 * `available` is the key set the payload actually carries. Passing it means a
 * payload written before a window existed degrades to the next narrowest one it
 * HAS, rather than missing the key and falling back to the 30-day window - which
 * for a 6mo selection would be narrower than the 90 it used to show.
 */
export function windowKeyForRange(range: Range, available?: Iterable<string>): RollingWindowKey {
  const offered = available ? new Set(available) : null;
  const usable = ROLLING_WINDOW_KEYS.filter((key) => !offered || offered.has(key));
  if (usable.length === 0) return "30";
  // "all" is unbounded, so every window fits and the widest one wins.
  const requestedDays = range === "all" ? Number.POSITIVE_INFINITY : Number(range);
  const withinRange = usable.filter((key) => Number(key) <= requestedDays);
  // Narrower than every computed window: take the narrowest rather than nothing.
  return withinRange.at(-1) ?? usable[0];
}

/**
 * Whether the selected range reads a window NARROWER than it names - true for
 * 12mo and All time while 365 is uncomputed. The headline states this plainly
 * rather than letting a reader believe the range changed the numbers below it.
 */
export function readsNarrowerWindow(tile: Tile, range: Range): boolean {
  if (range === "all") return true;
  const key = windowKeyForRange(range, tile.windows ? Object.keys(tile.windows) : undefined);
  return Number(key) < Number(range);
}

/**
 * The selected rolling window, with the tile's own top-level figures as the
 * fallback for a payload written before the producer emitted `windows`.
 *
 * The fallback carries `total: null` rather than a synthesised zero, so the
 * absolute-units headline says "n/a" instead of claiming nothing sold.
 */
export function selectWindow(tile: Tile, range: Range): RollingWindow {
  // Resolve against the keys this payload actually carries, so a stale payload
  // degrades to its widest usable window instead of to the 30-day one.
  const window = tile.windows?.[windowKeyForRange(range, tile.windows ? Object.keys(tile.windows) : undefined)];
  if (window) return window;
  return {
    value: tile.value,
    delta_vs_prior_period: tile.delta_vs_prior_period,
    band: tile.band,
    period: tile.period,
    products: [],
    total: null,
  };
}

export type UnitsDeltaLabel = "WoW" | "MoM" | "YoY";
export type UnitsDelta = { pct: number | null; label: UnitsDeltaLabel };

/**
 * Which comparison the absolute-units headline shows, keyed off the SELECTED
 * range so it matches the window the numbers are drawn from (locked with Damien
 * 2026-07-31). Showing a YoY change beside a 7-day count is how a reader
 * attributes a seasonal swing to last week's campaign.
 */
export function unitsDeltaForRange(total: WindowTotal | null, range: Range): UnitsDelta {
  if (range === "7") return { pct: total?.wow_pct ?? null, label: "WoW" };
  if (range === "30") return { pct: total?.mom_pct ?? null, label: "MoM" };
  // Every wider range reads a window of 90 days or more, and the only
  // comparison meaningful at that span is YoY.
  return { pct: total?.yoy_pct ?? null, label: "YoY" };
}

/** Rendered form of `unitsDeltaForRange`; empty when the producer had no value. */
export function formatUnitsDelta(delta: UnitsDelta): string {
  if (delta.pct === null) return "";
  const sign = delta.pct > 0 ? "+" : "";
  const arrow = delta.pct >= 0 ? "↑" : "↓";
  const formatted = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(delta.pct);
  return `${arrow} ${sign}${formatted}% ${delta.label}`;
}

/**
 * The products for the selected window, falling back to the payload's top-level
 * list only when the window carries none - the top-level list is the same shape
 * for the same period, so this is a fallback WITHIN one meaning, not across two.
 */
export function productsFor(window: RollingWindow, fallback: Product[]): Product[] {
  return window.products.length > 0 ? window.products : fallback;
}

/**
 * The series for a grain, with zero-filled periods dropped.
 *
 * `present: false` means the source query returned no row and the producer wrote
 * a 0. Plotting those alongside real measurements draws a cliff to zero that
 * never happened - the single most misreadable thing this chart could do - so
 * they are removed rather than rendered as data.
 *
 * A grain the producer has not backfilled yet returns an empty array, and the
 * chart says it has nothing for that grain instead of silently falling back to
 * another one. A silent fallback would label a monthly line as daily.
 */
export function seriesFor(tile: Tile, grain: Grain): SeriesPoint[] {
  const raw =
    grain === "daily"
      ? tile.series_daily
      : grain === "weekly"
        ? tile.series_weekly
        : // `series_monthly` mirrors `series`; fall back only within the SAME grain.
          tile.series_monthly?.length
          ? tile.series_monthly
          : tile.series;
  return (raw ?? []).filter((point) => point.present);
}

/**
 * The absolute-units series for a grain.
 *
 * Deliberately NO fallback to `series` / `series_monthly`: those hold the YoY
 * PERCENTAGE, and quietly charting a percentage on an axis labelled "units"
 * would be a fabricated measurement rather than a degraded one. A payload
 * written before the units series existed charts nothing and says so.
 */
export function unitsSeriesFor(tile: Tile, grain: Grain): SeriesPoint[] {
  const raw =
    grain === "daily"
      ? tile.series_units_daily
      : grain === "weekly"
        ? tile.series_units_weekly
        : tile.series_units_monthly;
  return (raw ?? []).filter((point) => point.present);
}

/**
 * The YoY-growth series as a per-grain bundle, present-filtered, for
 * `selectGrainSeries` (W4) - the single entry point pages use to window a
 * chart to the selected range.
 */
export function seriesBundle(tile: Tile): GrainBundle<SeriesPoint> {
  return {
    daily: (tile.series_daily ?? []).filter((point) => point.present),
    weekly: (tile.series_weekly ?? []).filter((point) => point.present),
    monthly: (tile.series_monthly?.length ? tile.series_monthly : (tile.series ?? [])).filter(
      (point) => point.present,
    ),
  };
}

/** The absolute-units series as a per-grain bundle, present-filtered. */
export function unitsSeriesBundle(tile: Tile): GrainBundle<SeriesPoint> {
  return {
    daily: (tile.series_units_daily ?? []).filter((point) => point.present),
    weekly: (tile.series_units_weekly ?? []).filter((point) => point.present),
    monthly: (tile.series_units_monthly ?? []).filter((point) => point.present),
  };
}

/**
 * The attention items that name a region.
 *
 * The producer encodes the region in the deep link (`/units-sold/uk?...`), not
 * as a field, so this reads the link rather than inventing a mapping. `mena` is
 * matched before `eu` would ever be tested against it because the slugs are
 * compared whole against the path segment, not searched for as substrings -
 * a naive `includes("eu")` matches nothing here but would the day a region
 * called "eu-west" exists.
 */
export function attentionItemsForRegion(items: AttentionItemLike[], region: string): AttentionItemLike[] {
  const slug = region.toLowerCase();
  return items.filter((item) => {
    const path = item.deep_link.toLowerCase().split("?")[0] ?? "";
    return path.split("/").includes(slug);
  });
}

type AttentionItemLike = { severity: AttentionSeverity; headline: string; deep_link: string };
